/**
 * useLiveSummary.js - Live Summary Layer for Scoreboard V9
 * 
 * PHASE 3.5 REQUIREMENT (Architect Directive):
 * Fetch roster data for each coach to show "X on field / X in locker room"
 * instead of hardcoded "all finished".
 * 
 * PATTERN: Copied from useHeadToHeadData.js
 * - Mode detection from /live-state status
 * - Roster fetch via /entry endpoint
 * - Section computation using getSection rules
 * - Concurrency control (max 3-4 simultaneous calls)
 * 
 * USAGE:
 * const { summaries, isLive, mode, loading } = useLiveSummary(poolId, coachUserIds);
 * // summaries = { [userId]: { onField: 2, locker: 1, finished: 3, topPlayer, highImpact } }
 */

import { useState, useEffect, useCallback, useRef } from 'react';

// =============================================================================
// Configuration
// =============================================================================

const API_BASE = import.meta.env.VITE_API_BASE_URL || '/api/v1';
const MAX_CONCURRENT_REQUESTS = 3; // Architect limit: max 3-4 simultaneous calls
const POLL_INTERVAL = 30000; // 30 seconds, matches leaderboard polling

// =============================================================================
// Mode Constants (from H2H pattern)
// =============================================================================

const MODE = {
  LIVE: 'live',
  PENDING: 'pending',
  HISTORICAL: 'historical',
};

// =============================================================================
// Section Logic (copied from useHeadToHeadData.js getSection)
// =============================================================================

/**
 * Determine which section a player allocation belongs to
 * Priority: resolution status first, then event_status
 */
const getSection = (allocation) => {
  // Check resolution status first (from actual API)
  const resStatus = allocation.resolution?.status?.toUpperCase();
  if (resStatus === 'HIT' || resStatus === 'MISS' || resStatus === 'PUSH') {
    return 'finished';
  }
  
  // Then check event_status if available
  const eventStatus = allocation.event_status?.toUpperCase();
  if (eventStatus === 'LIVE') return 'onField';
  if (eventStatus === 'UPCOMING') return 'locker';
  if (eventStatus === 'FINAL') return 'finished';
  
  // Fallback: check player/prop status patterns
  const status = allocation.status?.toLowerCase();
  if (status === 'live' || status === 'in_play') return 'onField';
  if (status === 'upcoming' || status === 'pending') return 'locker';
  
  // FIX B: Default to locker for pre-kick / locked states (safer than finished)
  return 'locker';
};

// =============================================================================
// Utility: Chunked Promise Execution (concurrency control)
// =============================================================================

/**
 * Execute promises in chunks to limit concurrency
 * @param {Array} items - Items to process
 * @param {Function} fn - Async function to call for each item
 * @param {number} concurrency - Max concurrent executions
 */
async function executeInChunks(items, fn, concurrency = MAX_CONCURRENT_REQUESTS) {
  const results = [];
  
  for (let i = 0; i < items.length; i += concurrency) {
    const chunk = items.slice(i, i + concurrency);
    const chunkResults = await Promise.all(chunk.map(fn));
    results.push(...chunkResults);
  }
  
  return results;
}

// =============================================================================
// Extract Summary from Roster
// =============================================================================

/**
 * Compute live summary from a coach's roster
 * @param {Array} roster - Array of allocation objects
 * @returns {Object} { onField, locker, finished, topPlayer, highImpact, results }
 */
const computeSummary = (roster = []) => {
  const summary = {
    onField: 0,
    locker: 0,
    finished: 0,
    topPlayer: null,    // Highest units player on field or in locker
    highImpact: false,  // Has any allocation >= 15 units
    players: {
      onField: [],
      locker: [],
    },
    // FINAL MODE: Game plan results
    results: {
      total: 0,      // Total allocations
      hits: 0,       // Resolved as HIT
      misses: 0,     // Resolved as MISS
      pushes: 0,     // Resolved as PUSH
      pending: 0,    // Not yet resolved
    },
  };
  
  roster.forEach(alloc => {
    const section = getSection(alloc);
    const units = alloc.units || 0;
    const playerName = alloc.player?.name || alloc.playerName || 'Unknown';
    
    // Count total allocations
    summary.results.total++;
    
    // Track resolution status for FINAL mode
    const resStatus = alloc.resolution?.status?.toUpperCase() || alloc.status?.toUpperCase();
    if (resStatus === 'HIT') {
      summary.results.hits++;
    } else if (resStatus === 'MISS') {
      summary.results.misses++;
    } else if (resStatus === 'PUSH') {
      summary.results.pushes++;
    } else {
      summary.results.pending++;
    }
    
    if (section === 'onField') {
      summary.onField++;
      summary.players.onField.push({ name: playerName, units });
    } else if (section === 'locker') {
      summary.locker++;
      summary.players.locker.push({ name: playerName, units });
    } else {
      summary.finished++;
    }
    
    // Track high impact (15+ units)
    if (units >= 15) {
      summary.highImpact = true;
    }
  });
  
  // Find top player (highest units, prefer on field over locker)
  const allActive = [
    ...summary.players.onField.map(p => ({ ...p, section: 'onField' })),
    ...summary.players.locker.map(p => ({ ...p, section: 'locker' })),
  ];
  
  if (allActive.length > 0) {
    allActive.sort((a, b) => {
      // Sort by units descending, then prefer onField
      if (b.units !== a.units) return b.units - a.units;
      return a.section === 'onField' ? -1 : 1;
    });
    summary.topPlayer = allActive[0];
  }
  
  return summary;
};

// =============================================================================
// Main Hook
// =============================================================================

/**
 * useLiveSummary - Fetch live state and roster summaries for all coaches
 * 
 * ARCHITECT REQUIREMENTS:
 * 1. Three-mode contract: LIVE / PENDING / HISTORICAL
 * 2. Roster data MUST be fetched in LIVE and PENDING modes
 * 3. Extract impact_remaining_units from live-state (NOT from balance)
 * 4. Hunters = coaches with (onField + locker) > 0 in LIVE/PENDING
 * 
 * @param {string} poolId - Pool ID
 * @param {Array<string>} coachUserIds - Array of user IDs to fetch rosters for
 * @param {Object} options - { enablePolling, pollingInterval }
 * @returns {Object} { summaries, liveStateEntries, isLive, isPending, isHistorical, mode, loading, error, refetch }
 */
export function useLiveSummary(poolId, coachUserIds = [], options = {}) {
  const {
    enablePolling = true,
    pollingInterval = POLL_INTERVAL,
  } = options;
  
  // State
  const [summaries, setSummaries] = useState({});
  const [liveStateEntries, setLiveStateEntries] = useState({}); // userId -> live-state entry data
  const [mode, setMode] = useState(MODE.PENDING);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [liveStateData, setLiveStateData] = useState(null);
  
  // Refs
  const abortControllerRef = useRef(null);
  const isMountedRef = useRef(true);
  const modeRef = useRef(MODE.PENDING);
  
  // ==========================================================================
  // Fetch Single Coach Roster
  // ==========================================================================
  
  const fetchCoachRoster = useCallback(async (userId, abortSignal) => {
    try {
      const response = await fetch(`${API_BASE}/pools/${poolId}/entry`, {
        headers: {
          'Content-Type': 'application/json',
          'X-User-ID': userId,
        },
        signal: abortSignal,
      });
      
      if (!response.ok) {
        console.log(`[LiveSummary] Roster fetch failed for ${userId}: ${response.status}`);
        return { userId, summary: null };
      }
      
      const data = await response.json();
      const roster = data.entry?.roster || data.roster || [];
      const summary = computeSummary(roster);
      
      return { userId, summary };
    } catch (err) {
      if (err.name === 'AbortError') throw err;
      console.log(`[LiveSummary] Error fetching roster for ${userId}:`, err.message);
      return { userId, summary: null };
    }
  }, [poolId]);
  
  // ==========================================================================
  // Main Fetch Function
  // ==========================================================================
  
  const fetchData = useCallback(async (isPolling = false) => {
    if (!poolId) {
      setError(new Error('No pool ID provided'));
      setLoading(false);
      return;
    }
    
    // Abort previous requests
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    
    const abortController = new AbortController();
    abortControllerRef.current = abortController;
    
    try {
      if (!isPolling) {
        setLoading(true);
      }
      setError(null);
      
      // Skip polling in HISTORICAL mode
      if (modeRef.current === MODE.HISTORICAL && isPolling) {
        console.log('[LiveSummary] Skipping poll (HISTORICAL mode)');
        return;
      }
      
      // 1. Fetch live-state to determine mode
      let lsData = null;
      let lsStatus = null;
      
      try {
        const lsResponse = await fetch(`${API_BASE}/pools/${poolId}/live-state?limit=100`, {
          headers: {
            'Content-Type': 'application/json',
            'X-User-ID': localStorage.getItem('userId') || 'test_user_001',
          },
          signal: abortController.signal,
        });
        
        if (abortController.signal.aborted) return;
        
        if (lsResponse.ok) {
          lsData = await lsResponse.json();
          lsStatus = 200;
        } else if (lsResponse.status === 404) {
          lsStatus = 404;
          console.log('[LiveSummary] live-state 404 → HISTORICAL mode');
        }
      } catch (lsErr) {
        if (lsErr.name === 'AbortError') return;
        console.log('[LiveSummary] live-state error:', lsErr.message);
      }
      
      // 2. Determine mode (copy H2H pattern)
      if (lsStatus === 404) {
        modeRef.current = MODE.HISTORICAL;
      } else if (lsData) {
        const status = lsData.status || lsData.snapshot?.status;
        if (status === 'ok' || status === 'stale') {
          modeRef.current = MODE.LIVE;
        } else {
          modeRef.current = MODE.PENDING;
        }
      }
      
      // 3. Extract live-state entries for each coach (for impact_remaining_units)
      // ARCHITECT: Use impact_remaining_units from live-state, NOT from balance
      const lsEntries = {};
      if (lsData) {
        // Extract "you" entry
        if (lsData.you) {
          lsEntries[lsData.you.user_id] = {
            score: lsData.you.score,
            projected_score: lsData.you.projected_score,
            rank: lsData.you.rank,
            impact_remaining_units: lsData.you.units?.impact_remaining_units ?? lsData.you.impact_remaining_units ?? 0,
          };
        }
        // Extract competitor entries
        const competitors = lsData.competitors || lsData.entries || [];
        competitors.forEach(entry => {
          const userId = entry.user_id || entry.userId;
          if (userId) {
            lsEntries[userId] = {
              score: entry.score,
              projected_score: entry.projected_score,
              rank: entry.rank,
              impact_remaining_units: entry.units?.impact_remaining_units ?? entry.impact_remaining_units ?? 0,
            };
          }
        });
      }
      
      if (abortController.signal.aborted) return;
      
      // 4. Fetch rosters for all coaches (with concurrency limit)
      // ARCHITECT: Roster data MUST be fetched in LIVE and PENDING modes
      const userIds = coachUserIds.filter(Boolean);
      
      if (userIds.length > 0) {
        const results = await executeInChunks(
          userIds,
          (userId) => fetchCoachRoster(userId, abortController.signal),
          MAX_CONCURRENT_REQUESTS
        );
        
        if (abortController.signal.aborted) return;
        
        // Build summaries map
        const newSummaries = {};
        results.forEach(({ userId, summary }) => {
          if (summary) {
            newSummaries[userId] = summary;
          }
        });
        
        if (isMountedRef.current) {
          setSummaries(newSummaries);
        }
      }
      
      // 5. Update state
      if (isMountedRef.current) {
        setLiveStateEntries(lsEntries);
        setMode(modeRef.current);
        setLiveStateData(lsData);
      }
      
    } catch (err) {
      if (err.name === 'AbortError') return;
      console.error('[LiveSummary] Error:', err);
      if (isMountedRef.current && !isPolling) {
        setError(err);
      }
    } finally {
      if (isMountedRef.current && !isPolling) {
        setLoading(false);
      }
    }
  }, [poolId, coachUserIds, fetchCoachRoster]);
  
  // ==========================================================================
  // Refetch function
  // ==========================================================================
  
  const refetch = useCallback(() => {
    modeRef.current = MODE.PENDING;
    fetchData(false);
  }, [fetchData]);
  
  // ==========================================================================
  // Effects
  // ==========================================================================
  
  // Initial fetch
  useEffect(() => {
    isMountedRef.current = true;
    fetchData(false);
    
    return () => {
      isMountedRef.current = false;
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [fetchData]);
  
  // Polling (only stops in HISTORICAL mode)
  useEffect(() => {
    const disablePolling = import.meta.env.VITE_DISABLE_POLLING === 'true';
    
    if (!enablePolling || disablePolling || !poolId) return;
    
    const intervalId = setInterval(() => {
      if (document.visibilityState === 'visible' && modeRef.current !== MODE.HISTORICAL) {
        fetchData(true);
      }
    }, pollingInterval);
    
    return () => clearInterval(intervalId);
  }, [enablePolling, pollingInterval, fetchData, poolId]);
  
  // Tab visibility
  useEffect(() => {
    if (!enablePolling) return;
    
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && modeRef.current !== MODE.HISTORICAL) {
        fetchData(true);
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [enablePolling, fetchData]);
  
  // ==========================================================================
  // Return
  // ==========================================================================
  
  return {
    summaries,
    liveStateEntries,  // ARCHITECT: impact_remaining_units from live-state
    isLive: mode === MODE.LIVE,
    isPending: mode === MODE.PENDING,
    isHistorical: mode === MODE.HISTORICAL,
    mode,
    loading,
    error,
    refetch,
    liveStateData,
  };
}

export default useLiveSummary;
